import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { resetPassword } from '@/api/auth';
import { toast } from '@/components/ui/Toast';
import { Button } from '@/components/ui/Button';

interface FieldErrors {
  email?: string[];
  token?: string[];
  password?: string[];
  password_confirmation?: string[];
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['auth', 'common']);

  const token = searchParams.get('token') ?? '';
  const emailFromUrl = searchParams.get('email') ?? '';

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isBrokenLink = !token || !emailFromUrl;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setGeneralError(null);
    setFieldErrors({});

    if (password !== passwordConfirmation) {
      setFieldErrors({ password_confirmation: [t('auth:resetPassword.errors.passwordMismatch')] });
      return;
    }

    setIsSubmitting(true);

    try {
      await resetPassword({
        email: emailFromUrl,
        token,
        password,
        password_confirmation: passwordConfirmation,
      });
      toast('success', t('auth:resetPassword.success'));
      navigate('/login', { replace: true });
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 422) {
          const errors = err.response?.data?.errors as FieldErrors | undefined;
          if (errors) {
            setFieldErrors(errors);
          } else {
            setGeneralError(t('auth:resetPassword.errors.invalidLink'));
          }
        } else {
          setGeneralError(t('auth:login.errors.unexpected'));
        }
      } else {
        setGeneralError(t('auth:login.errors.unexpected'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid =
    !isBrokenLink &&
    password.length >= 1 &&
    passwordConfirmation.length >= 1;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900">{t('common:appName')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('auth:resetPassword.title')}</p>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
          {isBrokenLink ? (
            <div className="text-center">
              <p className="text-sm text-gray-600">
                {t('auth:resetPassword.errors.expiredLink')}
              </p>
              <div className="mt-4">
                <Link
                  to="/forgot-password"
                  className="text-sm text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline"
                >
                  {t('auth:resetPassword.errors.requestNewLink')}
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {generalError && (
                <div role="alert" className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
                  {generalError}
                </div>
              )}

              <div className="mb-4">
                <label htmlFor="email-display" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth:forgotPassword.emailLabel')}
                </label>
                <input
                  id="email-display"
                  type="email"
                  value={emailFromUrl}
                  readOnly
                  className="block w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500 cursor-not-allowed"
                />
              </div>

              <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth:resetPassword.newPasswordLabel')}
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                  aria-invalid={!!fieldErrors.password}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 aria-[invalid=true]:border-red-400"
                  placeholder={t('auth:login.passwordPlaceholder')}
                  disabled={isSubmitting}
                />
                {fieldErrors.password && (
                  <p id="password-error" className="mt-1 text-xs text-red-600">
                    {fieldErrors.password[0]}
                  </p>
                )}
              </div>

              <div className="mb-6">
                <label
                  htmlFor="password-confirmation"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t('auth:resetPassword.confirmPasswordLabel')}
                </label>
                <input
                  id="password-confirmation"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  aria-describedby={
                    fieldErrors.password_confirmation ? 'confirm-error' : undefined
                  }
                  aria-invalid={!!fieldErrors.password_confirmation}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 aria-[invalid=true]:border-red-400"
                  placeholder={t('auth:login.passwordPlaceholder')}
                  disabled={isSubmitting}
                />
                {fieldErrors.password_confirmation && (
                  <p id="confirm-error" className="mt-1 text-xs text-red-600">
                    {fieldErrors.password_confirmation[0]}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? t('auth:resetPassword.submitting') : t('auth:resetPassword.submit')}
              </Button>

              <div className="mt-4 text-center">
                <Link
                  to="/login"
                  className="text-sm text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline"
                >
                  {t('auth:forgotPassword.backToSignIn')}
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
